import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ChevronDown, FolderInput, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/lib/api/core'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'
import { getDocumentDisplayIcon } from '@/lib/document-icons'
import {
  FolderPickerDialog,
  type WorkspaceOption,
} from '@/components/features/folder-picker-dialog'
import { toast } from 'sonner'
import { isSessionScratchPath } from '@/lib/session-scratch-path'
import { isModClick } from '@/lib/open-intent'
import { sessionFileMetaLabel } from './session-files-strip.utils'

export type SessionFileRow = {
  id: string
  title: string
  filePath: string
  kind: string
  documentType?: string
  updatedAt?: string
  isCanvas?: boolean
  isMemory?: boolean
  isSoulStack?: boolean
  /** Compact trailing metric from the API (file count, facts). */
  meta?: string | null
}

type SessionFilesStripProps = {
  conversationId: string | null | undefined
  onDocumentClick?: (documentId: string, event?: MouseEvent) => void
  defaultFolderPath?: string | null
  workspaces?: WorkspaceOption[]
  /** Menu checkbox: when true, heading stays visible even if the list is collapsed. */
  visible?: boolean
  onCountChange?: (count: number) => void
}

function fileBasename(filePath: string): string {
  return filePath.split('/').pop() || 'draft.md'
}

/** Picker currentPath is a file path so the tree selects that file's parent folder. */
function pickerCurrentPath(
  file: SessionFileRow,
  defaultFolderPath: string | null | undefined,
  workspaces: WorkspaceOption[],
): string {
  const basename = fileBasename(file.filePath)
  const hint = String(defaultFolderPath || '').replace(/\/+$/, '')
  if (hint && !isSessionScratchPath(hint)) {
    return `${hint}/${basename}`
  }
  const root = String(workspaces[0]?.contentRoot || '').replace(/\/+$/, '')
  if (root && !isSessionScratchPath(root)) {
    return `${root}/${basename}`
  }
  return file.filePath
}

function canFileOrBin(file: SessionFileRow): boolean {
  if (!file.isCanvas) return false
  if (file.isMemory) return false
  if (file.isSoulStack) return false
  if (file.kind === 'soul_stack') return false
  if (file.filePath.endsWith('soul-stack.md')) return false
  if (file.title.startsWith('Pointer:')) return false
  if (file.filePath.endsWith('-pointer.md')) return false
  return true
}

/** Compact agent-panel flyout listing session scratch files for the active chat. */
export function SessionFilesStrip({
  conversationId,
  onDocumentClick,
  defaultFolderPath,
  workspaces = [],
  visible = false,
  onCountChange,
}: SessionFilesStripProps) {
  const [files, setFiles] = useState<SessionFileRow[]>([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [fileTarget, setFileTarget] = useState<SessionFileRow | null>(null)

  /** Fetch session files from the API for the current conversation. */
  const loadFiles = useCallback(async () => {
    if (!conversationId) {
      setFiles([])
      onCountChange?.(0)
      return
    }
    setLoading(true)
    try {
      const res = await fetchWithAuth(`/api/conversations/${conversationId}/session-files`)
      if (!res.ok) {
        setFiles([])
        onCountChange?.(0)
        return
      }
      const data = await res.json()
      const next = Array.isArray(data.files) ? data.files : []
      setFiles(next)
      onCountChange?.(next.length)
    } catch {
      setFiles([])
      onCountChange?.(0)
    } finally {
      setLoading(false)
    }
  }, [conversationId, onCountChange])

  useEffect(() => {
    void loadFiles()
  }, [loadFiles])

  useEffect(() => {
    const handler = () => {
      void loadFiles()
    }
    window.addEventListener('session-files-changed', handler)
    return () => window.removeEventListener('session-files-changed', handler)
  }, [loadFiles])

  useEffect(() => {
    if (visible) setExpanded(true)
  }, [visible])

  const pickerPath = useMemo(
    () => (fileTarget ? pickerCurrentPath(fileTarget, defaultFolderPath, workspaces) : ''),
    [fileTarget, defaultFolderPath, workspaces],
  )

  /** Promote a canvas session doc into a workspace folder via POST /file. */
  const handleFileSelect = useCallback(
    async (folderPath: string) => {
      if (!conversationId || !fileTarget) return
      const res = await fetchWithAuth(
        `/api/conversations/${conversationId}/session-files/${fileTarget.id}/file`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderPath }),
        },
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(typeof data.error === 'string' ? data.error : 'Could not file this document')
        return
      }
      setFileTarget(null)
      window.dispatchEvent(new Event('session-files-changed'))
      void loadFiles()
    },
    [conversationId, fileTarget, loadFiles],
  )

  /** Hide a session doc from the strip via POST /bin. */
  const handleBin = useCallback(
    async (file: SessionFileRow) => {
      if (!conversationId) return
      const res = await fetchWithAuth(
        `/api/conversations/${conversationId}/session-files/${file.id}/bin`,
        { method: 'POST' },
      )
      if (res.ok) {
        window.dispatchEvent(new Event('session-files-changed'))
        void loadFiles()
      }
    },
    [conversationId, loadFiles],
  )

  if (!conversationId || !visible) {
    return null
  }

  return (
    <div
      className="border-b border-border bg-muted/40 px-3 pb-2 pt-1"
      data-testid={testIds.chatPane.sessionFiles}
    >
      <button
        type="button"
        className="mb-1 flex h-6 w-full items-center gap-2 text-left text-[11px] font-medium text-muted-foreground hover:text-foreground"
        data-testid={testIds.chatPane.sessionFilesToggle}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span>
          Session files
          {files.length > 0 ? (
            <span className="ml-1 text-muted-foreground/70">{files.length}</span>
          ) : null}
        </span>
        <ChevronDown
          className={cn(
            'ml-auto h-3.5 w-3.5 shrink-0 transition-transform',
            expanded && 'rotate-180',
          )}
        />
      </button>
      {expanded ? (
      <ul className="space-y-0.5">
        {files.map((file) => {
          const meta = sessionFileMetaLabel(file)
          return (
            <li
              key={file.id}
              className="flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-muted/70"
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                onClick={(event) => {
                  if (isModClick(event)) event.preventDefault()
                  onDocumentClick?.(file.id, event)
                }}
              >
                {getDocumentDisplayIcon(
                  {
                    kind: file.kind,
                    type: file.documentType,
                    filePath: file.filePath,
                  },
                  { className: 'h-3.5 w-3.5 shrink-0' },
                )}
                <span className="min-w-0 flex-1 truncate text-[13px] text-foreground/90">
                  {file.title}
                </span>
                {meta ? (
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
                    {meta}
                  </span>
                ) : null}
              </button>
              {canFileOrBin(file) ? (
                <div className="flex shrink-0 items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                    title="File to a workspace folder"
                    aria-label="File to a workspace folder"
                    data-testid={testIds.chatPane.sessionFilesFile}
                    onClick={() => setFileTarget(file)}
                  >
                    <FolderInput className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-red-500 dark:hover:text-red-400"
                    title="Remove from this chat (file stays in the workspace)"
                    aria-label="Remove from this chat"
                    data-testid={testIds.chatPane.sessionFilesBin}
                    onClick={() => void handleBin(file)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>
      ) : null}
      <FolderPickerDialog
        open={Boolean(fileTarget)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setFileTarget(null)
        }}
        currentPath={pickerPath}
        workspaces={workspaces}
        title="File to workspace"
        description="Choose a folder. This leaves the session strip and becomes a normal Browse document."
        confirmLabel="File here"
        onSelect={(folderPath) => {
          void handleFileSelect(folderPath)
        }}
      />
    </div>
  )
}
